import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AssetUrlService } from '@app/core/services/asset-url.service';
import { BrandingService } from '@app/core/services/branding.service';
import { MicrophonePermissionService } from '@app/core/services/microphone-permission.service';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import { VoiceMessageRecorder } from '@app/shared/models/voice-message.recorder.model';
import { AudioUtil, sanitizeAudioContextSinkId } from '@app/shared/utils/audio.util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { finalize, firstValueFrom } from 'rxjs';

import { AudioSettings } from '../../models/settings.models';
import { SettingsPage } from '../../models/settings-page';
import { SettingsService } from '../../services/settings.service';

type Type = 'Call' | 'Notification' | 'Voicemail' | 'OutboundRingBack';

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    BrandingColorDirective,
    MatButtonModule,
    TranslateModule,
    MatSliderModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatInputModule,
    MatTooltipModule,
  ],
  selector: 'app-audio',
  templateUrl: './audio.component.html',
  styleUrls: ['./audio.component.scss'],
})
export class AudioComponent extends SettingsPage implements OnInit, AfterViewInit, OnDestroy {
  @Input() displayedInPopupWindow = false;

  protected showSliders = false;
  private voiceMessageRecorder: VoiceMessageRecorder;
  private ringtoneIncomingCall: string;
  private ringtoneOutgoingCall: string;
  private notificationSound: string;
  private audioElement = new Audio() as AudioUtil;
  private inputSound = new Audio() as AudioUtil;
  protected isRecording: boolean | null = false; //null means playing back the recorded sound
  protected counter = 0;
  protected currentMicrophoneVolume: number = 0;
  protected currentSpeakerVolume: number = 0;
  private audioContext = new AudioContext();
  private audioSource: MediaElementAudioSourceNode | MediaStreamAudioSourceNode | null = null;
  protected audioForm: FormGroup;
  protected activeTest?: Type;

  private _objectURL?: string;
  private get objectURL(): string | undefined {
    return this._objectURL;
  }
  private set objectURL(url: string) {
    this.tryReleaseObjectURL();
    this._objectURL = url;
  }

  protected readonly BrandingField = BrandingField;
  protected defaultBrandingSettings = defaultBrandingSettings;

  private get microphoneDevice(): string {
    return this.audioForm.get('microphoneDevice')!.value;
  }

  protected get speakerDevice(): string {
    return this.audioForm.get('speakerDevice')!.value;
  }

  private get notificationSoundVolume(): number {
    return this.audioForm.get('notificationSoundVolume')!.value;
  }

  private get ringingSoundVolume(): number {
    return this.audioForm.get('ringingSoundVolume')!.value;
  }

  private get outboundRingBackVolume(): number {
    return this.audioForm.get('outboundRingBackVolume')!.value;
  }

  protected get notificationSpeakerDevice() {
    return this.audioForm.get('notificationSpeakerDevice')!.value;
  }

  protected get voicemailPlaybackSpeaker(): string {
    return this.audioForm.get('voicemailPlaybackSpeaker')!.value;
  }

  constructor(
    public settingsService: SettingsService,
    private appConfigService: AppConfigService,
    private assetUrlService: AssetUrlService,
    private formBuilder: FormBuilder,
    private microphonePermissionService: MicrophonePermissionService,
    protected brandingService: BrandingService
  ) {
    super();
    this.ringtoneIncomingCall = this.assetUrlService.getSoundUrl('ringtone-incoming-call.wav');
    this.ringtoneOutgoingCall = this.assetUrlService.getSoundUrl('ringtone-outgoing-call.wav');
    this.notificationSound = this.assetUrlService.getSoundUrl('notification_simple-01.wav');
    this.audioForm = this.createFormGroup(this.settingsService.getDefaultAudioSettings());

    this.voiceMessageRecorder = new VoiceMessageRecorder(microphonePermissionService);

    this.voiceMessageRecorder.state$.pipe(untilDestroyed(this)).subscribe(async (state) => {
      if (state === 'recording') {
        this.isRecording = true;
        this.counter = 1;
      }
      if (state === 'finished') {
        const audioBlob = await this.voiceMessageRecorder.getRecordingAsBlob();
        this.objectURL = URL.createObjectURL(audioBlob);
        this.inputSound.src = this.objectURL;
        this.isRecording = null;

        const intervalId = setInterval(() => {
          this.counter--;
          if (this.counter === 0) {
            this.isRecording = false;
            clearInterval(intervalId);
          }
        }, 1000);

        await this.settingsService.playAudio(this.inputSound, this.ringingSoundVolume, this.speakerDevice);
      }
    });
    this.voiceMessageRecorder.elapsedTime$.pipe(untilDestroyed(this)).subscribe((time) => {
      this.counter = time;
    });
  }

  ngOnInit() {
    this.settingsService.audioSettings$.pipe(untilDestroyed(this)).subscribe(async (settings: AudioSettings) => {
      settings.quietRinging = false; // TODO: remove this when we return to the quietRinging option
      settings = this.settingsService.audioReplaceNullsWithDefaults(settings);
      this.audioForm = this.createFormGroup(settings);
      await this.scanDevices();

      if (!this.settingsService.isSetSinkIdSupported || this.settingsService.outputDevices.length === 0) {
        this.audioForm.controls['speakerDevice'].disable();
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      /* This is the workaround for sliders misaligment between knob and highlighted part of the track
         especially when AudioComponent is opened from OngoingCallComponent. This is known MatSlider bug. */
      this.showSliders = true;
    }, 200);
  }

  ngOnDestroy() {
    this.disabled = true;
    this.stopInputTest();
    this.stopOutputTest();
    this.tryReleaseObjectURL();

    this.audioElement.pause();
    this.audioElement.src = '';
    this.inputSound.pause();
    this.inputSound.src = '';
  }

  private createFormGroup(audioSettings: AudioSettings): FormGroup {
    return this.formBuilder.group({
      notificationSoundVolume: [audioSettings.notificationSoundVolume],
      ringingSoundVolume: [audioSettings.ringingSoundVolume],
      quietRinging: [audioSettings.quietRinging],
      speakerDevice: [audioSettings.speakerDevice],
      notificationSpeakerDevice: [audioSettings.notificationSpeakerDevice],
      voicemailPlaybackSpeaker: [audioSettings.voicemailPlaybackSpeaker],
      microphoneDevice: [audioSettings.microphoneDevice],
      enableDualRinging: [audioSettings.enableDualRinging],
      noiseSuppressedEnabled: [audioSettings.noiseSuppressedEnabled],
      outboundRingBackVolume: [audioSettings.outboundRingBackVolume],
    });
  }

  protected async scanDevices() {
    try {
      if (await this.microphonePermissionService.check()) {
        await this.settingsService.scanForAudioDevices();
        this.setDefaultDevices();
      }
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  }

  private setDefaultDevices(): void {
    const inputDevices = this.settingsService.inputDevices;
    if (inputDevices.length > 0) {
      const defaultMicrophoneDevice = inputDevices[0].deviceId;
      const isDefaultMicrophoneAvailable = inputDevices.some((device) => device.deviceId === this.microphoneDevice);
      this.audioForm.patchValue({
        microphoneDevice: isDefaultMicrophoneAvailable ? this.microphoneDevice : defaultMicrophoneDevice,
      });
    } else {
      this.audioForm.controls['microphoneDevice'].disable();
    }

    const outputDevices = this.settingsService.outputDevices;
    if (outputDevices.length > 0) {
      const defaultSpeakerDevice = outputDevices[0].deviceId;
      const isDefaultSpeakerAvailable = outputDevices.some((device) => device.deviceId === this.speakerDevice);
      const resolvedSpeakerDevice = isDefaultSpeakerAvailable ? this.speakerDevice : defaultSpeakerDevice;
      const isDefaultNotificationSpeakerAvailable = outputDevices.some(
        (device) => device.deviceId === this.notificationSpeakerDevice
      );
      const isVoicemailPlaybackSpeakerAvailable = outputDevices.some(
        (device) => device.deviceId === this.voicemailPlaybackSpeaker
      );
      this.audioForm.patchValue({
        speakerDevice: resolvedSpeakerDevice,
        notificationSpeakerDevice: isDefaultNotificationSpeakerAvailable
          ? this.notificationSpeakerDevice
          : defaultSpeakerDevice,
        voicemailPlaybackSpeaker: isVoicemailPlaybackSpeakerAvailable
          ? this.voicemailPlaybackSpeaker
          : resolvedSpeakerDevice,
      });
    }
  }

  private async setupAudioContextAndNodes(
    audio: MediaStream | HTMLAudioElement,
    device?: {
      deviceId: string;
      isOutputDevice: boolean;
    }
  ) {
    this.audioSource =
      audio instanceof HTMLAudioElement
        ? this.audioContext.createMediaElementSource(audio)
        : this.audioContext.createMediaStreamSource(audio);

    const destination = this.audioContext.createMediaStreamDestination();
    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 2048;

    // When the audio graph is set up using AudioContext, we need to call
    // setSinkId on it otherwise audio gets piped through the OS-default audio device.
    if ('setSinkId' in AudioContext.prototype && device && device.isOutputDevice) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (this.audioContext as any).setSinkId(sanitizeAudioContextSinkId(device.deviceId));
    }
    this.audioSource?.connect(analyser);
    audio instanceof HTMLAudioElement && analyser.connect(this.audioContext.destination);

    return { destination, analyser };
  }

  private drawAudioMeter(analyser: AnalyserNode, updateVolumeCallback: (volume: number) => void) {
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const drawMeter = () => {
      requestAnimationFrame(drawMeter);
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / bufferLength;
      const percentage = Math.min(100, average);
      const volume = Math.floor(percentage / 10);
      updateVolumeCallback(volume);
    };
    drawMeter();
  }

  protected async testInputSound() {
    this.stopInputTest();
    this.stopOutputTest();

    this.voiceMessageRecorder.setDeviceId(this.microphoneDevice);
    await this.voiceMessageRecorder.startRecording(10);

    if (this.voiceMessageRecorder.analyser) {
      this.drawAudioMeter(this.voiceMessageRecorder.analyser, (volume) => (this.currentMicrophoneVolume = volume));
    }
  }

  protected async testOutputDevice(device: string, type: Type) {
    this.stopInputTest();
    this.stopOutputTest();

    this.audioElement = new Audio(this.ringtoneIncomingCall) as AudioUtil;
    this.activeTest = type;
    this.audioElement.loop = true;
    const soundVolume = type === 'Call' ? this.ringingSoundVolume : this.notificationSoundVolume;
    await this.settingsService.playAudio(this.audioElement, soundVolume, device);
    const { analyser } = await this.setupAudioContextAndNodes(this.audioElement, {
      deviceId: device,
      isOutputDevice: true,
    });
    this.drawAudioMeter(analyser, (volume) => (this.currentSpeakerVolume = volume));
  }

  protected stopOutputTest() {
    this.activeTest = undefined;
    this.audioElement.pause();
  }

  protected stopInputTest() {
    this.activeTest = undefined;
    this.inputSound.pause();

    this.voiceMessageRecorder.stopRecording();
  }

  protected async onChange(type: Type) {
    this.stopInputTest();
    this.stopOutputTest();

    const { src, volume, speaker } = this.getPreviewConfig(type);

    this.audioElement = new Audio(src) as AudioUtil;
    this.audioElement.loop = false;
    await this.settingsService.playAudio(this.audioElement, volume, speaker);

    this.disabled = false;
  }

  private getPreviewConfig(type: Type): { src: string; volume: number; speaker: string } {
    switch (type) {
      case 'Call':
        return {
          src: this.ringtoneIncomingCall,
          volume: this.ringingSoundVolume,
          speaker: this.notificationSpeakerDevice,
        };
      case 'OutboundRingBack':
        return {
          src: this.ringtoneOutgoingCall,
          volume: this.outboundRingBackVolume,
          speaker: this.speakerDevice,
        };
      default:
        return {
          src: this.notificationSound,
          volume: this.notificationSoundVolume,
          speaker: this.notificationSpeakerDevice,
        };
    }
  }

  async save() {
    this.stopInputTest();
    this.stopOutputTest();
    this.loading = true;
    await firstValueFrom(
      this.appConfigService.saveAudioSettings(this.audioForm.value).pipe(finalize(() => (this.loading = false)))
    );
  }

  private tryReleaseObjectURL() {
    if (this._objectURL) {
      URL.revokeObjectURL(this._objectURL);
      this._objectURL = undefined;
    }
  }
}
